"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { RefreshCw } from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/fun-ui/Button";
import { Tip } from "@/components/fun-ui/Tip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/fun-ui/Dialog";
import { ZoteroIcon } from "@/components/fun-ui/icons";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { validateCountBeforeUpload } from "@/components/paper-upload/core/upload-validator";
import {
  readZoteroPaperFiles,
  zoteroReadFailureSummary,
  type ZoteroPaperReadProgress,
} from "@/lib/desktop/zotero-import";
import { cn } from "@/lib/utils";
import {
  getZoteroStatus,
  isZoteroDesktopRuntime,
  listZoteroPapers,
  readZoteroPaperFile,
  type ZoteroLibrary,
  type ZoteroPaper,
  type ZoteroStatus,
} from "@/lib/desktop/zotero";
import { registerZoteroSource } from "@/lib/desktop/zotero-links";

import type { UploadPlugin, UploadPluginRenderProps } from "./plugin-interface";

const ALL_COLLECTIONS = "__all__";

function getErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  return "读取 Zotero 文库失败，请重试。";
}

function paperMeta(paper: ZoteroPaper) {
  const metadata = [
    paper.creators.slice(0, 3).join("、"),
    paper.date,
    paper.publicationTitle,
  ].filter(Boolean);
  return metadata.join(" · ");
}

function ZoteroPluginTrigger({
  config,
  uploadedCount = 0,
  disabled,
  onUpload,
  onSubDialogChange,
}: UploadPluginRenderProps) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<ZoteroStatus | null>(null);
  const [library, setLibrary] = useState<ZoteroLibrary | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [importError, setImportError] = useState("");
  const [query, setQuery] = useState("");
  const [collectionKey, setCollectionKey] = useState(ALL_COLLECTIONS);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [readProgress, setReadProgress] = useState<ZoteroPaperReadProgress>({
    completed: 0,
    total: 0,
  });

  const setDialogOpen = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    onSubDialogChange?.(nextOpen);
  }, [onSubDialogChange]);

  const loadLibrary = useCallback(async () => {
    setLoading(true);
    setError("");
    setImportError("");
    setLibrary(null);
    setSelectedKeys(new Set());
    try {
      const nextStatus = await getZoteroStatus();
      setStatus(nextStatus);
      if (nextStatus.running && nextStatus.apiEnabled) {
        setLibrary(await listZoteroPapers());
      }
    } catch (loadError) {
      setError(getErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    void getZoteroStatus()
      .then((nextStatus) => {
        if (!active) return;
        setStatus(nextStatus);
      })
      .catch(() => {
        // 检测异常由打开选择器后的错误态处理，不等同于 Zotero 未启动或未开启通信。
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (open) {
      void loadLibrary();
    }
  }, [open, loadLibrary]);

  useEffect(() => {
    const handleWindowFocus = async () => {
      if (loading || importing) return;

      try {
        const nextStatus = await getZoteroStatus();
        setStatus(nextStatus);

        if (!nextStatus.running || !nextStatus.apiEnabled) {
          setLibrary(null);
          setSelectedKeys(new Set());
          return;
        }

        // 选择器仍开着时，恢复焦点后直接加载文库；仅上传弹窗打开时只更新 Tip 状态。
        if (open && !library) {
          setLoading(true);
          setError("");
          try {
            setLibrary(await listZoteroPapers());
          } catch (focusError) {
            setError(getErrorMessage(focusError));
          } finally {
            setLoading(false);
          }
        }
      } catch (focusError) {
        if (open) setError(getErrorMessage(focusError));
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    return () => window.removeEventListener("focus", handleWindowFocus);
  }, [importing, library, loading, open]);

  const visiblePapers = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    return (library?.papers ?? []).filter((paper) => {
      const matchesCollection = collectionKey === ALL_COLLECTIONS
        || paper.collections.includes(collectionKey);
      if (!matchesCollection) {
        return false;
      }
      if (!normalizedQuery) {
        return true;
      }
      return [
        paper.title,
        paper.filename,
        paper.doi,
        paper.publicationTitle,
        ...paper.creators,
      ].some((value) => value.toLocaleLowerCase().includes(normalizedQuery));
    });
  }, [collectionKey, library, query]);

  const filterableCollections = useMemo(() => {
    const usedCollectionKeys = new Set(
      (library?.papers ?? []).flatMap((paper) => paper.collections),
    );
    return (library?.collections ?? []).filter((collection) => (
      usedCollectionKeys.has(collection.key)
    ));
  }, [library]);

  const selectedPapers = useMemo(() => {
    const papersByKey = new Map(
      (library?.papers ?? []).map((paper) => [paper.attachmentKey, paper]),
    );
    return Array.from(selectedKeys)
      .map((key) => papersByKey.get(key))
      .filter((paper): paper is ZoteroPaper => Boolean(paper));
  }, [library, selectedKeys]);

  const visibleAllSelected = visiblePapers.length > 0
    && visiblePapers.every((paper) => selectedKeys.has(paper.attachmentKey));

  const togglePaper = (attachmentKey: string, checked: boolean) => {
    setImportError("");
    setSelectedKeys((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(attachmentKey);
      } else {
        next.delete(attachmentKey);
      }
      return next;
    });
  };

  const toggleVisiblePapers = (checked: boolean) => {
    setImportError("");
    setSelectedKeys((previous) => {
      const next = new Set(previous);
      visiblePapers.forEach((paper) => {
        if (checked) {
          next.add(paper.attachmentKey);
        } else {
          next.delete(paper.attachmentKey);
        }
      });
      return next;
    });
  };

  const importSelected = async () => {
    if (!selectedPapers.length || importing) {
      return;
    }

    const countValidation = validateCountBeforeUpload(
      selectedPapers.length,
      config,
      uploadedCount,
    );
    if (!countValidation.valid) {
      toast.error(countValidation.error.message);
      return;
    }

    setImporting(true);
    setImportError("");
    setReadProgress({ completed: 0, total: selectedPapers.length });
    try {
      const { imported, failures } = await readZoteroPaperFiles(
        selectedPapers,
        readZoteroPaperFile,
        setReadProgress,
      );
      const files = imported.map(({ file, paper }) => {
        registerZoteroSource(file, paper);
        return file;
      });

      if (!files.length) {
        setImportError(zoteroReadFailureSummary(failures, 0));
        return;
      }

      await onUpload(files);
      setDialogOpen(false);
      if (failures.length) {
        toast(zoteroReadFailureSummary(failures, files.length));
      }
    } catch (importError) {
      const message = getErrorMessage(importError);
      setImportError(message);
      toast.error(message);
    } finally {
      setImporting(false);
    }
  };

  const setupTip = status && !status.running ? (
    <div className="w-72 space-y-2">
      <p className="font-medium">未检测到正在运行的 Zotero</p>
      <p className="text-sm leading-5 text-muted-foreground">
        请先启动 Zotero。返回 Findin 后会自动重新检测，无需刷新页面。
      </p>
    </div>
  ) : status && !status.apiEnabled ? (
    <div className="w-[480px] max-w-[calc(100vw-3rem)] space-y-2">
      <Image
        src="/images/zotero-local-api-tip.png"
        alt="在 Zotero 高级设置中开启本机应用通信"
        width={1600}
        height={1256}
        className="h-auto w-full rounded-md"
      />
      <p className="px-1 text-sm leading-5">
        勾选后返回 Findin，将自动重新检测，无需刷新页面。
      </p>
    </div>
  ) : "";

  return (
    <Dialog open={open} onOpenChange={setDialogOpen}>
      <Tip
        label={setupTip}
        side="top"
        truncate={false}
        contentClassName="max-w-none bg-background p-2 text-foreground shadow-xl"
        asChild
      >
        <DialogTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className="min-w-[130px] gap-2 border-primary text-primary hover:bg-primary/5 hover:text-primary"
          >
            <ZoteroIcon size={16} />
            从 Zotero 导入
          </Button>
        </DialogTrigger>
      </Tip>
      <DialogContent
        className="w-[calc(100vw-2rem)] max-w-3xl gap-0 overflow-hidden p-0"
        showCloseButton
      >
        <DialogHeader className="border-b px-6 py-5">
          <DialogTitle className="flex items-center gap-2">
            <ZoteroIcon size={20} />
            从 Zotero 导入
          </DialogTitle>
          <DialogDescription>
            读取本机 Zotero 文库中的 PDF，不会修改或移动 Zotero 文件。
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-[440px] min-w-0 max-w-full overflow-hidden px-6 py-5">
          {loading ? (
            <div className="flex h-[400px] items-center justify-center gap-2 text-sm text-muted-foreground">
              <RefreshCw className="size-4 animate-spin" />
              正在连接 Zotero…
            </div>
          ) : null}

          {!loading && error ? (
            <div className="flex h-[400px] flex-col items-center justify-center gap-4 text-center">
              <p className="max-w-md text-sm text-destructive">{error}</p>
              <Button type="button" variant="outline" onClick={() => void loadLibrary()}>
                重新检测
              </Button>
            </div>
          ) : null}

          {!loading && !error && status && (!status.running || !status.apiEnabled) ? (
            <div className="flex h-[400px] flex-col items-center justify-center gap-4 text-center">
              <div className="space-y-2">
                <p className="font-medium">{status.message}</p>
                {status.running ? (
                  <p className="max-w-lg text-sm leading-6 text-muted-foreground">
                    请在 Zotero 中打开“设置 → 高级”，启用“允许本机其他应用与 Zotero 通信”。
                    返回 Findin 后将自动重新检测，无需刷新页面。
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    请先启动 Zotero 桌面应用。返回 Findin 后将自动重新检测，无需刷新页面。
                  </p>
                )}
              </div>
              <Button type="button" variant="outline" onClick={() => void loadLibrary()}>
                <RefreshCw className="mr-2 size-4" />
                重新检测
              </Button>
            </div>
          ) : null}

          {!loading && !error && status?.apiEnabled && library ? (
            <div className="min-w-0 max-w-full space-y-4 overflow-hidden">
              <div className="flex min-w-0 items-center justify-between gap-4">
                <div className="min-w-0 truncate text-sm text-muted-foreground">
                  已连接 Zotero{status.version ? ` ${status.version}` : ""}，找到 {library.papers.length} 个 PDF
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => void loadLibrary()}
                  aria-label="刷新 Zotero 文库"
                >
                  <RefreshCw className="size-4" />
                </Button>
              </div>

              <div
                className={cn(
                  "grid min-w-0 grid-cols-1 gap-3",
                  filterableCollections.length > 0 && "sm:grid-cols-[minmax(0,1fr)_220px]",
                )}
              >
                <Input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="搜索标题、作者、DOI"
                  className="min-w-0"
                />
                {filterableCollections.length > 0 ? (
                  <select
                    value={collectionKey}
                    onChange={(event) => setCollectionKey(event.target.value)}
                    className="h-10 min-w-0 rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="筛选 Zotero 分类"
                  >
                    <option value={ALL_COLLECTIONS}>全部分类</option>
                    {filterableCollections.map((collection) => (
                      <option key={collection.key} value={collection.key}>
                        {collection.name}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>

              <div className="flex items-center gap-3 border-b pb-3 text-sm">
                <Checkbox
                  checked={visibleAllSelected}
                  onCheckedChange={(checked) => toggleVisiblePapers(checked === true)}
                  aria-label="选择当前筛选结果"
                />
                <span>选择当前结果（{visiblePapers.length}）</span>
              </div>

              <ScrollArea className="h-[300px] min-w-0 max-w-full overflow-hidden pr-3 [&_[data-slot=scroll-area-viewport]>div]:!block">
                {visiblePapers.length ? (
                  <div className="min-w-0 max-w-full divide-y overflow-hidden">
                    {visiblePapers.map((paper) => {
                      const checked = selectedKeys.has(paper.attachmentKey);
                      return (
                        <label
                          key={paper.attachmentKey}
                          className={cn(
                            "flex min-w-0 max-w-full cursor-pointer items-start gap-3 overflow-hidden px-2 py-3 transition-colors hover:bg-accent/60",
                            checked && "bg-primary/5",
                          )}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(nextChecked) => togglePaper(
                              paper.attachmentKey,
                              nextChecked === true,
                            )}
                            aria-label={`选择 ${paper.title}`}
                            className="mt-1"
                          />
                          <div className="min-w-0 flex-1 space-y-1">
                            <p className="truncate text-sm font-medium" title={paper.title}>
                              {paper.title || paper.filename}
                            </p>
                            <p className="truncate text-xs text-muted-foreground">
                              {paperMeta(paper) || paper.filename}
                            </p>
                            {paper.doi ? (
                              <p className="truncate text-xs text-muted-foreground">DOI：{paper.doi}</p>
                            ) : null}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    当前条件下没有可导入的 PDF
                  </div>
                )}
              </ScrollArea>
              {importError ? (
                <p className="text-sm text-destructive" role="alert">
                  {importError}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter className="items-center justify-between border-t px-6 py-4 sm:justify-between">
          <span className="text-sm text-muted-foreground">已选择 {selectedPapers.length} 篇</span>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              取消
            </Button>
            <Button
              type="button"
              disabled={!selectedPapers.length || importing || !status?.apiEnabled}
              onClick={() => void importSelected()}
            >
              {importing
                ? `正在读取 ${readProgress.completed}/${readProgress.total} 篇…`
                : `导入 ${selectedPapers.length} 篇`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const zoteroPlugin: UploadPlugin = {
  id: "zotero",
  name: "从 Zotero 导入",
  source: "zotero",
  renderTrigger: (props) => <ZoteroPluginTrigger {...props} />,
  isSupported: () => isZoteroDesktopRuntime(),
};
